import type { Category, ThreeDElement } from '@/composables/useLayerSelect'
import type { LabelSceneElement, LayerSceneElement, SceneElement } from '@/entities/model3d'
import { Vector2, Shape, ShapeGeometry, Matrix4 } from 'three'
import type { UseThreeDViewer } from '~/composables/useThreeDViewer'
import type { TextLabel } from '@/lib/three-d-resources/labels-manager'
import type { Layer } from '~/composables/useThreeDLayerGroups'
import type { DrillDataSetLayerGroup } from '~/composables/useDrillDataSetGroups'
import type { DrillLabelLayer, DrillCSVFile, DrillHole } from '~/entities/drillLabel'
import { exhaustive } from '~/lib/three-d-resources/utils'

/**
 * Replace newlines and carriage returns with a unicode return symbol.
 * @param input string
 * @returns the string with unicode symbols added
 */
export function toReturnSymbol(str: string): string {
  return str.trim().replace(/[\r\n]/gm, '↩')
}

/**
 * Replace unicode return symbol with newlines.
 * @param input string
 * @returns the multiline string with newlines added
 */
export function toMultiLine(str: string): string {
  return str.replace(/↩/gm, '\n').trim()
}

export function isTextLabel(v: ThreeDElement): v is TextLabel {
  return 'content' in v
}

export function isDrillLabel(v: ThreeDElement): v is DrillLabelLayer {
  return 'holeId' in v && 'id' in v
}

export function isLayer(v: ThreeDElement): v is Layer {
  return !('content' in v) && !('categoryName' in v) && !('site_id' in v) && !('holeId' in v)
}

export function isCategory(v: ThreeDElement): v is Category {
  return 'categoryName' in v
}

export function isLayerCategory(v: ThreeDElement): v is Category {
  return 'categoryName' in v && v.type === 'layer'
}

export function isDrillDataSetGroup(v: ThreeDElement): v is DrillDataSetLayerGroup {
  return 'type' in v && v.type === 'drill'
}

export function isDrillCSVFile(v: ThreeDElement): v is DrillCSVFile {
  return 'fileId' in v
}

export function isDrillHole(v: ThreeDElement): v is DrillHole {
  return 'holeId' in v
}

export function selectableId(v: ThreeDElement): string {
  if (isLayer(v)) return v.nodeId.toString()
  if (isTextLabel(v)) return `${v.nodeId}-${v.id}`
  if (isDrillLabel(v)) return `drill-${v.id}`
  if (isDrillHole(v)) return v.holeId
  if (isCategory(v)) return v.categoryName
  if (isDrillDataSetGroup(v)) return v.name
  if (isDrillCSVFile(v)) return v.fileId
  else exhaustive(v)
}

export function isScopedLabel(el: SceneElement | null): el is LabelSceneElement {
  return !!el && 'labelId' in el
}

export function isScopedLayer(el: SceneElement | null): el is LayerSceneElement {
  return !!el && 'nodeId' in el
}

export function toggleElementVisibility({ elements, threeDViewer }: { elements: ThreeDElement[]; threeDViewer: UseThreeDViewer }) {
  // Set all hidden if _any_ are visible, or visible if _all_ are hidden.
  const visible = !elements.some(element => {
    if (isTextLabel(element) || isDrillLabel(element)) return element.visible
    if (isLayer(element)) return threeDViewer.world.isNodeVisible(element.nodeId)
  })

  // Set all layers to the same visibility
  elements.filter(isLayer).forEach(el => {
    threeDViewer.setLayerVisibility([{ nodeId: el.nodeId, visible }])
  })

  // Set all labels to the same visibility
  elements.filter(isTextLabel).forEach(el => {
    threeDViewer.labelsManager.updateLabel(el.id, { visible })
  })

  elements.filter(isDrillLabel).forEach(el => {
    threeDViewer.world.drillDataSetManager?.drillLabelManager.setVisible(el.id as number, visible)
  })
}

// goes from bottom left(-1,-1) to top right(1,1)
export function pointerEventToVec2(e: MouseEvent) {
  const { x: x0, y: y0, width, height } = (e.currentTarget as Element).getBoundingClientRect()
  return new Vector2(((e.pageX - x0) / width) * 2 - 1, -((e.pageY - y0) / height) * 2 + 1)
}

export function domElementsAt(e: MouseEvent) {
  return document.elementsFromPoint(e.pageX, e.pageY) as HTMLElement[]
}

export function toggleGroupLabelVisibility({ elements, threeDViewer }: { elements: (Layer | TextLabel)[]; threeDViewer: UseThreeDViewer }) {
  if (!elements.every(el => isLayer(el) || isTextLabel(el))) return

  const visible = elements.some(layer => isTextLabel(layer) && layer.visible)

  elements.filter(isTextLabel).forEach(layer => {
    for (const label of threeDViewer.labelsManager.getLabelsForNode(layer.nodeId)) {
      threeDViewer.labelsManager.updateLabel(label.id, { visible: !visible })
    }
  })
}

// create a rounded rectangle geometry
// https://discourse.threejs.org/t/roundedrectangle-squircle/28645
export function rectangleRounded(width: number, height: number, radius: number, smoothness: number, autoScale: boolean = false) {
  // helper const's
  const offsetX = 0
  const offsetY = height
  const w = width
  const h = height
  const r = radius
  const s = smoothness
  const wi = w / 2 - r // inner width
  const hi = h / 2 - r // inner height

  const midPoints = [
    // top midpoint
    w / 2 - 0.5,
    hi - h / 2 + r + 0.5,
    // left midpoint
    -wi + w / 2 - r - 0.5,
    -h / 2 + 0.5,
    // bottom midpoint
    w / 2 - 0.5,
    -hi - h / 2 - r + 0.5,
    // right midpoint
    wi + w / 2 + r - 0.5,
    -h / 2 + 0.5,
  ]

  let phi, cos, sin, xc, yc

  const shape = new Shape()

  for (let i = 0; i < 4; i++) {
    xc = i < 1 || i > 2 ? wi : -wi
    yc = i < 2 ? hi : -hi

    for (let j = 0; j <= s; j++) {
      phi = (Math.PI / 2) * (i + j / s)
      cos = Math.cos(phi)
      sin = Math.sin(phi)
      let x = xc + r * cos + width / 2
      let y = yc + r * sin - height / 2
      x = x < 0 ? x + 0.5 : x - 0.5
      y = y > 0 ? y - 0.5 : y + 0.5

      if (i == 0 && j == 0) {
        shape.moveTo(x, y)
      } else {
        shape.lineTo(x, y)
      }
    }

    shape.lineTo(midPoints[i * 2], midPoints[i * 2 + 1])
  }

  const shapeGeometry = new ShapeGeometry(shape)

  // scale the uv's to the [0, 1] range
  const uv = shapeGeometry.getAttribute('uv')
  for (let i = 0; i < uv.count; i++) {
    uv.setXY(i, uv.getX(i) / width, (uv.getY(i) + height) / height)
  }

  if (autoScale) shapeGeometry.applyMatrix4(new Matrix4().makeTranslation(offsetX, offsetY, 0))
  return shapeGeometry
}
